import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getWritingQuestions, deleteWritingQuestion } from '../../api/writingApi';
import {
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Article as ArticleIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernDataGrid from '../../components/common/ModernDataGrid';
import BreadcrumbsNav from '../../components/common/BreadcrumbsNav';

const WritingQuestionsList = () => {
  const { writingId } = useParams();
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [viewDetails, setViewDetails] = useState(null);
  const { showToast } = useToast();

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);
        const writingData = await getWritingQuestions(writingId);
        console.log('Writing data:', writingData);
        // Handle both possible data structures
        const questionsData = writingData.Questions || writingData.questions || [];
        setQuestions(questionsData);
      } catch (error) {
        console.error('Failed to fetch questions:', error);
        setQuestions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [writingId]);

  const handleEdit = (questionId) => {
    navigate(`/writings/${writingId}/questions/${questionId}/edit`);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteWritingQuestion(writingId, deleteTarget._id);
      setQuestions((prev) => prev.filter((q) => q._id !== deleteTarget._id));
      showToast('Question deleted successfully!', 'success');
    } catch (error) {
      console.error('Failed to delete question:', error);
      showToast('Failed to delete question', 'error');
    } finally {
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return (
      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 8, textAlign: 'center' }}>
          <CircularProgress size={48} />
          <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
            Loading questions...
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      <BreadcrumbsNav
        links={[{ label: 'Writings', onClick: () => navigate('/writings') }]}
        current="Questions"
      />
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              backgroundColor: 'primary.main',
              width: 48,
              height: 48,
              mr: 2,
            }}
          >
            <ArticleIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Writing Questions
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Manage writing task questions and content
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <ModernButton
            onClick={() => navigate(`/writings/${writingId}/questions/new`)}
            startIcon={<AddIcon />}
            variant="contained"
          >
            Create New Question
          </ModernButton>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={`${questions.length} Questions`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 500 }}
            />
          </Box>
        </Box>
      </Box>

      {/* Questions Data Grid */}
      {questions.length === 0 ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 8, textAlign: 'center' }}>
            <Avatar sx={{ backgroundColor: 'grey.300', width: 64, height: 64, mx: 'auto', mb: 2 }}>
              <ArticleIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ mb: 1, color: 'text.primary' }}>
              No Questions Found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
              No questions found. Create your first writing question to get started.
            </Typography>
            <ModernButton
              onClick={() => navigate(`/writings/${writingId}/questions/new`)}
              startIcon={<AddIcon />}
              variant="contained"
            >
              Create First Question
            </ModernButton>
          </CardContent>
        </Card>
      ) : (
        <ModernDataGrid
          rows={questions}
          getRowId={(row) => row._id}
          columns={[
            { 
              field: 'Label', 
              headerName: 'Question Content', 
              flex: 1,
              renderCell: (params) => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar
                    sx={{
                      backgroundColor: 'primary.main',
                      width: 32,
                      height: 32,
                      fontSize: '0.875rem',
                    }}
                  >
                    Q
                  </Avatar>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {params.value ? params.value.replace(/<[^>]*>/g, '').substring(0, 100) + '...' : 'N/A'}
                  </Typography>
                </Box>
              ),
            },
            {
              field: 'actions',
              headerName: 'Actions',
              sortable: false,
              flex: 1.5,
              renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="View Details">
                    <IconButton
                      onClick={() => setViewDetails(params.row)}
                      sx={{
                        color: 'info.main',
                        '&:hover': {
                          backgroundColor: 'info.50',
                        },
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Edit Question">
                    <IconButton
                      onClick={() => handleEdit(params.row._id)}
                      sx={{
                        color: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.50',
                        },
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Question">
                    <IconButton
                      onClick={() => setDeleteTarget(params.row)}
                      sx={{
                        color: 'error.main',
                        '&:hover': {
                          backgroundColor: 'error.50',
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              ),
            },
          ]}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={Boolean(deleteTarget)} 
        onClose={() => setDeleteTarget(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Confirm Deletion
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <Typography>
            Are you sure you want to delete this question? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <ModernButton
            onClick={() => setDeleteTarget(null)}
            variant="outlined"
          >
            Cancel
          </ModernButton>
          <ModernButton
            onClick={handleDelete}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
              },
            }}
          >
            Delete
          </ModernButton>
        </DialogActions>
      </Dialog>

      {/* Question Details Dialog */}
      <Dialog 
        open={Boolean(viewDetails)} 
        onClose={() => setViewDetails(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, pb: 1 }}>
          Question Details
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {viewDetails && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ mb: 2 }}>
                <strong>Content:</strong>
              </Typography>
              <Box 
                sx={{ 
                  p: 2, 
                  backgroundColor: 'grey.50', 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
                dangerouslySetInnerHTML={{ __html: viewDetails.Label || 'No content' }}
              />
              {viewDetails.Image && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Image:</strong>
                  </Typography>
                  <img 
                    src={viewDetails.Image} 
                    alt="Question image" 
                    style={{ maxWidth: '100%', height: 'auto', borderRadius: '8px' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <ModernButton
            onClick={() => setViewDetails(null)}
            variant="outlined"
          >
            Close
          </ModernButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WritingQuestionsList;
